import { useMemo } from 'react'

import { useSharedUsersQuery } from 'features/alerts/hooks/use-shared-users-query'
import { useTrendDetectionService } from 'features/trend-detection/service/hook'
import type {
  AgentCollaborator,
  AgentCollaboratorsResponse,
} from 'features/trend-detection/types'

/** Agent kinds that support sharing. */
export type SharableAgentType = 'issue_detection' | 'benchmarking' | 'insight'

/**
 * Per-kind sharing calls, so `AgentSharingSection`/`AgentSharingModal`
 * dispatch by lookup rather than by branching.
 *
 * Replaces an `isBenchmarking` boolean that was read at six points across two
 * components. A third kind would have turned each of those into a nested
 * ternary, and the failure mode of that shape is quiet: miss one and an insight
 * agent silently calls the ISSUE endpoints with its uuid, which 404s at best.
 * A lookup makes a missing kind a type error instead.
 */
export type SharingCalls = {
  fetch: (
    projectId: string,
    agentUuid: string,
  ) => Promise<AgentCollaboratorsResponse>
  share: (
    projectId: string,
    agentUuid: string,
    emails: string[],
  ) => Promise<AgentCollaboratorsResponse>
  unshare: (
    projectId: string,
    agentUuid: string,
    emails: string[],
  ) => Promise<AgentCollaboratorsResponse>
}

export function sharingCallsFor(
  service: ReturnType<typeof useTrendDetectionService>,
  agentType: SharableAgentType,
): SharingCalls {
  const byType: Record<SharableAgentType, SharingCalls> = {
    issue_detection: {
      fetch: (p, a) => service.fetchAgentCollaborators(p, a),
      share: (p, a, e) => service.shareAgent(p, a, e),
      unshare: (p, a, e) => service.unshareAgent(p, a, e),
    },
    benchmarking: {
      fetch: (p, a) => service.fetchBenchmarkingAgentCollaborators(p, a),
      share: (p, a, e) => service.shareBenchmarkingAgent(p, a, e),
      unshare: (p, a, e) => service.unshareBenchmarkingAgent(p, a, e),
    },
    insight: {
      fetch: (p, a) => service.fetchInsightAgentCollaborators(p, a),
      share: (p, a, e) => service.shareInsightAgent(p, a, e),
      unshare: (p, a, e) => service.unshareInsightAgent(p, a, e),
    },
  }
  return byType[agentType]
}

export function useProjectShareEmails(projectId: string | undefined): string[] {
  const { data } = useSharedUsersQuery(projectId ?? '', !!projectId)
  return useMemo(() => {
    if (!data) return []
    return [...data.admins, ...data.shared_users, ...data.yogi_employees].sort()
  }, [data])
}

/** Two-letter tile initials — prefers the person's name (first + last), falls
 * back to the first two characters of the email when no name is on file. */
export function initialsFor(collaborator: AgentCollaborator): string {
  const source = collaborator.full_name.trim() || collaborator.email
  const [first, second] = source.split(/\s+/).filter(Boolean)
  if (first && second) return (first.charAt(0) + second.charAt(0)).toUpperCase()
  return source.slice(0, 2).toUpperCase()
}
